import React, { useState, useEffect, useRef, useContext } from "react";
import { Result, Button, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { getAuthMethod } from "../services/HttpServices";
import WhatsAppConnect from "../components/whatsapp/WhatsAppConnect";
import WhatsAppSetupFlow from "../components/whatsapp/WhatsAppSetupFlow";
import WhatsAppInbox from "../components/whatsapp/WhatsAppInbox";
import WhatsAppCampaigns from "../components/whatsapp/WhatsAppCampaigns";
import WhatsAppCreateCampaign from "../components/whatsapp/WhatsAppCreateCampaign";
import WhatsAppRegistrationForm from "../components/whatsapp/WhatsAppRegistrationForm";
import { AuthContext } from "../context/AuthContext";
import { userRoles } from "../config";

const WhatsApp = ({ view = "inbox" }) => {
  const { user, isWhatsAppView } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [onboardingState, setOnboardingState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  const isPollingPaused = useRef(false);
  const [connectionData, setConnectionData] = useState(null);

  useEffect(() => {
    if (user?.role !== userRoles?.admin && !isWhatsAppView) {
      navigate("/dashboard-view");
      return;
    }
  }, [navigate, user, isWhatsAppView]);

  useEffect(() => {
    let timeoutId;
    
    const fetchStatus = () => {
      // Don't poll if we're forcing retry manually
      if (isPollingPaused.current) return;
      
      getAuthMethod("/api/whatsapp/accounts/status")
        .then((response) => {
          if (response.data) {
            setConnectionData(response.data);
            const connected = !!response.data.connected;
            setIsConnected(connected);
            setOnboardingState(response.data.onboardingState);
            
            // Guard: if not connected, redirect to /whatsapp to onboard
            if (!connected && window.location.pathname !== "/whatsapp") {
              navigate("/whatsapp");
            }
            
            // Auto-poll if still in pending state
            const pending = ['TOKEN_EXCHANGED', 'WABA_DISCOVERED', 'PHONE_SYNCED', 'EMBEDDED_SIGNUP_COMPLETE', 'REGISTRATION_PENDING'];
            if (response.data.onboardingState && pending.includes(response.data.onboardingState)) {
              timeoutId = setTimeout(fetchStatus, 3000);
            }
          }
        })
        .catch((err) => console.error("Error fetching WABA status", err))
        .finally(() => setIsLoading(false));
    };

    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    const error = params.get("error");
    const correlationId = params.get("correlation_id");

    if (status === "success") {
      window.history.replaceState({}, document.title, window.location.pathname);
      // We must wait for fetchStatus to determine the actual onboardingState before disabling isLoading
      fetchStatus();
    } else if (error) {
      console.error(`WhatsApp connection error: ${error} (Correlation ID: ${correlationId})`);
      setIsLoading(false);
      
      let friendlyMessage = "An unexpected error occurred while connecting your WhatsApp account.";
      if (error === "invalid_state") {
        friendlyMessage = "The authentication session is invalid or expired. Please try connecting again.";
      } else if (error === "duplicate_callback") {
        friendlyMessage = "This request was already processed. If your account isn't connected, please try again.";
      } else if (error === "server_error") {
        friendlyMessage = "We encountered a problem communicating with Meta. Please try again later.";
      } else if (error === "wabaAlreadyRegistered") {
        friendlyMessage = (
          <div className="tw-text-left tw-max-w-md tw-mx-auto tw-mt-4 tw-text-gray-600">
            <p>This WhatsApp Business Account is currently connected to another BeyondLeadz workspace.</p>
            <p>To connect it to this workspace:</p>
            <ol className="tw-pl-5 tw-mb-4 tw-space-y-1">
              <li>Log in to the workspace where the account is currently connected.</li>
              <li>Disconnect the WhatsApp account.</li>
              <li>Return here and complete the connection process again.</li>
            </ol>
            <p className="tw-text-xs tw-text-gray-500 tw-mt-4">
              For security reasons, BeyondLeadz does not automatically transfer WhatsApp accounts between workspaces.
            </p>
          </div>
        );
      }
      
      setErrorMessage(friendlyMessage);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      fetchStatus();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-min-h-[500px]">
        <Spin size="large" />
      </div>
    );
  }

  if (errorMessage) {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    const title = error === "wabaAlreadyRegistered" 
      ? "WhatsApp Account Already Connected" 
      : "WhatsApp Connection Failed";

    return (
      <Result
        status="warning"
        title={title}
        subTitle={errorMessage}
        extra={[
          <Button type="primary" key="retry" onClick={() => setErrorMessage(null)}>
            Try Again
          </Button>
        ]}
      />
    );
  }

  if (isConnected) {
    if (view === "create-campaign" || view === "edit-campaign") {
      return (
        <WhatsAppCreateCampaign
          onBack={() => navigate(-1)}
          onCancel={() => navigate(-1)}
          connectionData={connectionData}
        />
      );
    }

    if (view === "campaigns") {
      return (
        <WhatsAppCampaigns
          onBack={() => navigate(-1)}
          onCreateCampaign={() => navigate("/whatsapp-campaigns/create")}
          connectionData={connectionData}
        />
      );
    }

    return (
      <WhatsAppInbox 
        onOpenCampaigns={() => navigate("/whatsapp-campaigns")} 
        connectionData={connectionData} 
        onDisconnect={() => setIsConnected(false)} 
      />
    );
  }

  if (onboardingState === 'REGISTRATION_PENDING' || onboardingState === 'REGISTRATION_FAILED') {
    return (
      <WhatsAppRegistrationForm 
        connectionData={connectionData}
        onSuccess={() => {
          setOnboardingState('COMPLETED');
          setIsConnected(true);
        }}
        onCancel={() => {
          isPollingPaused.current = false;
          setOnboardingState('DISCONNECTED');
        }}
      />
    );
  }

  // If we are in an intermediate state, show a loading spinner
  const pendingStates = ['TOKEN_EXCHANGED', 'WABA_DISCOVERED', 'PHONE_SYNCED', 'PHONE_DISCOVERED', 'EMBEDDED_SIGNUP_COMPLETE'];
  if (onboardingState && pendingStates.includes(onboardingState)) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-min-h-[500px]">
        <Spin size="large" />
        <p className="tw-mt-4 tw-text-lg tw-text-text-primary tw-font-medium">
          Completing WhatsApp setup...
        </p>
        <p className="tw-mt-2 tw-text-sm tw-text-sub-text tw-max-w-md tw-text-center">
          We are securely provisioning your WhatsApp Business Account. This typically takes a few seconds, but may occasionally take longer.
        </p>
      </div>
    );
  }

  return isConnecting ? (
    <WhatsAppSetupFlow 
      onBack={() => setIsConnecting(false)} 
      onFinish={() => {
        setIsConnecting(false);
        setIsConnected(true);
        navigate("/whatsapp");
      }}
    />
  ) : (
    <WhatsAppConnect onConnect={() => setIsConnecting(true)} />
  );
};

export default WhatsApp;
